import DollarSignIcon from "@redotech/redo-icons/currency-dollar-circle.svg";
import LayoutDashboardIcon from "@redotech/redo-icons/dashboard.svg";
import RedoLogo from "@redotech/redo-icons/redo-logo-cutout.svg";
import SettingsIcon from "@redotech/redo-icons/settings.svg";
import ShieldIcon from "@redotech/redo-icons/shield-tick.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { memo, useState } from "react";
import { NavLink } from "react-router-dom";

import { useAuthData } from "../auth/auth-data-context";
import * as css from "./sidebar.module.css";

/**
 * Left rail navigation. NavLink-driven (decision 7A) so:
 *   - active state is route-driven, no manual tracking
 *   - middle-click + right-click "open in new tab" work as users expect
 *   - semantic <a> for screen readers
 *
 * Per the user's scope decisions: no "Install the app" promo card,
 * chargebacks is the only product surfaced, settings sits in the
 * footer next to the partner identity.
 */
export const Sidebar = memo(function Sidebar() {
  const { partner } = useAuthData();
  const [failedProfileImageUrl, setFailedProfileImageUrl] = useState<
    string | null
  >(null);

  const initials =
    partner?.name
      .split(" ")
      .map((w) => w[0])
      .filter((c): c is string => !!c)
      .slice(0, 2)
      .join("")
      .toUpperCase() ?? "—";

  return (
    <aside className={css.sidebar}>
      <div className={css.brand}>
        <a
          className={css.brandLink}
          href="https://www.redo.com"
          rel="noreferrer"
          target="_blank"
        >
          <RedoLogo className={css.brandLogo} />
          <span className={css.brandSubmark}>Partners</span>
        </a>
      </div>

      <nav className={css.nav}>
        <NavLink
          className={({ isActive }) =>
            `${css.navItem} ${isActive ? css.navItemActive : ""}`
          }
          to="/dashboard"
        >
          <RedoIcon
            className={css.navIcon}
            Icon={LayoutDashboardIcon}
            size={14}
          />
          Dashboard
        </NavLink>
        <NavLink
          className={({ isActive }) =>
            `${css.navItem} ${isActive ? css.navItemActive : ""}`
          }
          to="/earnings"
        >
          <RedoIcon className={css.navIcon} Icon={DollarSignIcon} size={14} />
          Earnings
        </NavLink>

        <div className={css.section}>Products</div>
        <NavLink
          className={({ isActive }) =>
            `${css.navItem} ${isActive ? css.navItemActive : ""}`
          }
          to="/products/chargebacks"
        >
          <RedoIcon className={css.navIcon} Icon={ShieldIcon} size={14} />
          Chargebacks
        </NavLink>
      </nav>

      <div className={css.footer}>
        {partner?.profileImageUrl &&
        failedProfileImageUrl !== partner.profileImageUrl ? (
          <img
            alt=""
            aria-hidden
            className={css.avatar}
            onError={() => setFailedProfileImageUrl(partner.profileImageUrl)}
            src={partner.profileImageUrl}
          />
        ) : (
          <div aria-hidden className={css.avatar}>
            {initials}
          </div>
        )}
        <div className={css.identity}>
          <div className={css.identityName}>{partner?.name ?? "—"}</div>
          <div className={css.identityEmail}>{partner?.email ?? ""}</div>
        </div>
        <NavLink
          aria-label="Settings"
          className={({ isActive }) =>
            `${css.gear} ${isActive ? css.gearActive : ""}`
          }
          title="Settings"
          to="/settings"
        >
          <RedoIcon Icon={SettingsIcon} size={14} />
        </NavLink>
      </div>
    </aside>
  );
});
